import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";

export function NewDocModal({ templates, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [tplKeys, setTplKeys] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);
  const toggleTpl = (key) => setTplKeys((s) => s.includes(key) ? s.filter((k) => k !== key) : [...s, key]);
  const submit = async () => {
    if (!tplKeys.length || !employeeId) { toast.error("Tick at least one document and pick an employee"); return; }
    setSaving(true);
    const created = [];
    const failed = [];
    // Keep the user's tick order so the edit flow moves through them in sequence
    for (const key of tplKeys) {
      try {
        const { data } = await api.post("/hr/documents", { template_type: key, employee_id: employeeId, field_values: {} });
        created.push(data);
      } catch (e) {
        failed.push(`${templates.find((t) => t.key === key)?.title || key}: ${e.response?.data?.detail || "failed"}`);
      }
    }
    if (created.length) toast.success(`${created.length} draft${created.length === 1 ? "" : "s"} created${created.length > 1 ? " — edit each one and use SAVE & NEXT" : ""}`);
    failed.forEach((f) => toast.error(f));
    setSaving(false);
    if (created.length) onCreated(created);
  };
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-start justify-center p-2 sm:p-3 pt-[76px] overflow-y-auto">
      <div className="brut-card max-w-md w-full p-4 bg-white" onClick={(e) => e.stopPropagation()} data-testid="new-doc-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-black">Issue New Document{tplKeys.length > 1 ? "s" : ""}</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-mono block mb-1">DOCUMENTS — TICK ALL YOU WANT TO ISSUE</label>
            <div className="max-h-56 overflow-y-auto border-2 border-zinc-900 rounded-sm divide-y divide-zinc-200" data-testid="new-doc-template-list">
              {templates.map((t) => (
                <label key={t.key} className={`flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-zinc-50 ${tplKeys.includes(t.key) ? "bg-emerald-50" : ""}`}>
                  <input
                    type="checkbox"
                    checked={tplKeys.includes(t.key)}
                    onChange={() => toggleTpl(t.key)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid={`new-doc-tpl-${t.key}`}
                  />
                  <span className="font-medium">{t.title}</span>
                  <span className="ml-auto text-[10px] font-mono text-zinc-400 uppercase">{t.category || ""}</span>
                </label>
              ))}
            </div>
            {tplKeys.length > 0 && (
              <div className="text-[10px] font-mono text-emerald-700 mt-1" data-testid="new-doc-selected-count">
                {tplKeys.length} selected: {tplKeys.map((k) => templates.find((t) => t.key === k)?.title || k).join(" · ")}
              </div>
            )}
          </div>
          <div>
            <label className="label-mono block">EMPLOYEE</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="brut-input w-full text-sm mt-0.5" data-testid="new-doc-employee">
              <option value="">— select —</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="brut-btn-secondary px-3 py-1.5 text-xs">CANCEL</button>
            <button onClick={submit} disabled={saving} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400" data-testid="new-doc-create">
              {saving ? "…" : tplKeys.length > 1 ? `CREATE ${tplKeys.length} DRAFTS` : "CREATE DRAFT"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

